import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField } from "@/components/FormKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft, Plus, Trash2, Send, CheckCircle2, GitBranch, Copy } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const STATUS = {
  rascunho: { label: "Rascunho", cls: "bg-slate-100 text-slate-600" },
  em_analise: { label: "Em análise", cls: "bg-amber-100 text-amber-700" },
  aprovado: { label: "Aprovado", cls: "bg-emerald-100 text-emerald-700" },
  revisado: { label: "Revisado (arquivado)", cls: "bg-slate-200 text-slate-500" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.rascunho;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${s.cls}`} data-testid="budget-status-badge">{s.label}</span>;
}

export default function BudgetPage() {
  const { hasPermission } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [costCenters, setCostCenters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newYear, setNewYear] = useState(new Date().getFullYear() + 1);
  const [copyYear, setCopyYear] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/budgets").then((r) => setBudgets(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/cost-centers").then((r) => setCostCenters(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => c.type === "despesa"))).catch(() => {});
  }, []);

  const openBudget = async (id) => {
    const { data } = await api.get(`/budgets/${id}`);
    setSelected(data);
    setLines((data.lines || []).map((l) => ({ cost_center_id: l.cost_center_id || "", category_id: l.category_id || "", months: [...(l.months || Array(12).fill(0))] })));
  };

  const editable = selected && ["rascunho", "em_analise"].includes(selected.status) && hasPermission("budget.edit");

  const addLine = () => setLines((ls) => [...ls, { cost_center_id: "", category_id: "", months: Array(12).fill(0) }]);
  const removeLine = (i) => setLines((ls) => ls.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const setMonth = (i, m, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, months: l.months.map((x, mi) => mi === m ? (v === "" ? 0 : parseFloat(v)) : x) } : l));
  const lineTotal = (l) => l.months.reduce((s, x) => s + (Number(x) || 0), 0);
  const grandTotal = useMemo(() => lines.reduce((s, l) => s + lineTotal(l), 0), [lines]);

  const save = async () => {
    if (lines.some((l) => !l.cost_center_id || !l.category_id)) {
      toast.error("Cada linha precisa de Centro de Custo e Categoria selecionados.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/budgets/${selected.id}`, { year: selected.year, lines });
      toast.success("Orçamento salvo.");
      await openBudget(selected.id); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const doAction = async (path, msg) => {
    try { await api.post(`/budgets/${selected.id}/${path}`); toast.success(msg); const id = selected.id; setSelected(null); load(); if (path === "revise") { const list = await api.get("/budgets"); const rev = list.data.find((b) => b.year === selected.year && !b.superseded); if (rev) openBudget(rev.id); } else { openBudget(id); } }
    catch (e) { toast.error(apiError(e)); }
  };
  const createBudget = async (e) => {
    e.preventDefault();
    const y = parseInt(newYear);
    if (!y || y < 2000 || y > 2200) { toast.error("Informe um exercício válido (2000–2200)."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/budgets", { year: y, lines: [], copy_from_year: copyYear ? parseInt(copyYear) : null });
      toast.success("Orçamento criado."); setNewOpen(false); setCopyYear(""); load(); openBudget(data.id);
    } catch (e2) { toast.error(apiError(e2)); } finally { setSaving(false); }
  };
  const removeBudget = async (b) => {
    if (!window.confirm(`Excluir o orçamento ${b.year} (rascunho)?`)) return;
    try { await api.delete(`/budgets/${b.id}`); toast.success("Orçamento excluído."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const ccOpts = costCenters;
  const catOpts = categories;

  if (selected) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected(null)} data-testid="budget-back" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">Orçamento {selected.year} <span className="text-sm font-normal text-slate-400">v{selected.version}</span></h1>
              <div className="mt-1"><StatusBadge status={selected.status} /></div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {editable && <button onClick={addLine} data-testid="budget-add-line" className="border border-slate-200 text-slate-600 px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-slate-50"><Plus className="h-4 w-4" /> Linha</button>}
            {editable && <button onClick={save} disabled={saving} data-testid="budget-save" className="bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Salvar</button>}
            {selected.status === "rascunho" && hasPermission("budget.edit") && <button onClick={() => doAction("submit", "Enviado para análise.")} data-testid="budget-submit" className="border border-amber-300 text-amber-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-amber-50"><Send className="h-4 w-4" /> Enviar p/ análise</button>}
            {["em_analise", "rascunho"].includes(selected.status) && hasPermission("budget.approve") && <button onClick={() => doAction("approve", "Orçamento aprovado.")} data-testid="budget-approve" className="border border-emerald-300 text-emerald-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-emerald-50"><CheckCircle2 className="h-4 w-4" /> Aprovar</button>}
            {selected.status === "aprovado" && hasPermission("budget.revise") && <button onClick={() => doAction("revise", "Revisão criada.")} data-testid="budget-revise" className="border border-brand text-brand px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-brand-light"><GitBranch className="h-4 w-4" /> Criar revisão</button>}
          </div>
        </div>

        {lines.length === 0 ? <EmptyState text="Nenhuma linha. Adicione linhas (centro de custo + categoria) para orçar." /> : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="text-sm" data-testid="budget-lines-table">
                <thead>
                  <tr className="bg-slate-50/80 border-b border-slate-200 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-3 text-left sticky left-0 bg-slate-50/80 min-w-[170px]">Centro de Custo</th>
                    <th className="px-3 py-3 text-left min-w-[150px]">Categoria</th>
                    {MONTHS.map((m) => <th key={m} className="px-2 py-3 text-right min-w-[92px]">{m}</th>)}
                    <th className="px-3 py-3 text-right min-w-[110px]">Total</th>
                    {editable && <th className="px-2 py-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className="border-b border-slate-100" data-testid={`budget-line-${i}`}>
                      <td className="px-3 py-2 sticky left-0 bg-white">
                        <Select value={l.cost_center_id || "none"} onValueChange={(v) => setLine(i, "cost_center_id", v === "none" ? "" : v)} disabled={!editable}>
                          <SelectTrigger className="text-xs h-9" data-testid={`budget-line-cc-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">—</SelectItem>{ccOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        <Select value={l.category_id || "none"} onValueChange={(v) => setLine(i, "category_id", v === "none" ? "" : v)} disabled={!editable}>
                          <SelectTrigger className="text-xs h-9" data-testid={`budget-line-cat-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="none">—</SelectItem>{catOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      {l.months.map((v, mi) => (
                        <td key={mi} className="px-1 py-2">
                          <input type="number" step="0.01" value={v || ""} disabled={!editable} onChange={(e) => setMonth(i, mi, e.target.value)} data-testid={`budget-line-${i}-m${mi}`} className="w-[86px] bg-white border border-slate-200 rounded px-1.5 py-1.5 text-xs font-mono text-right focus:outline-none focus:border-brand disabled:bg-slate-50 disabled:text-slate-400" placeholder="0" />
                        </td>
                      ))}
                      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-800 whitespace-nowrap">{brl(lineTotal(l))}</td>
                      {editable && <td className="px-2 py-2"><button onClick={() => removeLine(i)} data-testid={`budget-line-remove-${i}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="h-4 w-4" /></button></td>}
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold">
                    <td className="px-3 py-3 sticky left-0 bg-slate-50 text-slate-700" colSpan={2}>TOTAL ANUAL</td>
                    {MONTHS.map((_, mi) => <td key={mi} className="px-2 py-3 text-right font-mono text-xs text-slate-600">{brl(lines.reduce((s, l) => s + (Number(l.months[mi]) || 0), 0)).replace("R$", "").trim()}</td>)}
                    <td className="px-3 py-3 text-right font-mono text-brand" data-testid="budget-grand-total">{brl(grandTotal)}</td>
                    {editable && <td></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selected.history?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <h3 className="font-heading font-semibold text-slate-900 mb-2 text-sm">Histórico</h3>
            <div className="space-y-1.5">
              {selected.history.slice().reverse().map((h, i) => (
                <div key={i} className="text-xs text-slate-500 flex gap-2"><span className="text-slate-400">{fmtDate(h.at)}</span><span className="font-medium text-slate-700">{h.user_name}</span><span>— {h.note}</span></div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Orçamento Anual" subtitle="Elaboração e acompanhamento do orçamento por exercício, centro de custo e categoria" />
      <Toolbar search="" setSearch={() => {}} onNew={hasPermission("budget.create") ? () => setNewOpen(true) : null} newLabel="Novo Orçamento" newTestId="budget-new" searchTestId="budget-search" />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : budgets.length === 0 ? <EmptyState text="Nenhum orçamento cadastrado. Clique em Novo Orçamento." />
        : (
        <TableShell testId="budgets-table" headers={[
          { label: "Exercício" }, { label: "Versão" }, { label: "Status" }, { label: "Total Anual", align: "right" }, { label: "Atualizado" }, { label: "Ações", align: "right" },
        ]}>
          {budgets.map((b) => (
            <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`budget-row-${b.id}`}>
              <td className="px-4 py-3.5 font-semibold text-slate-800">{b.year}</td>
              <td className="px-4 py-3.5 text-slate-500">v{b.version}{b.superseded ? " (arquivada)" : ""}</td>
              <td className="px-4 py-3.5"><StatusBadge status={b.status} /></td>
              <td className="px-4 py-3.5 text-right font-mono font-semibold text-slate-900">{brl(b.total)}</td>
              <td className="px-4 py-3.5 text-slate-500 text-xs">{fmtDate(b.updated_at)}</td>
              <td className="px-4 py-3.5">
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => openBudget(b.id)} data-testid={`budget-open-${b.id}`} className="text-brand hover:underline text-sm font-medium">Abrir</button>
                  {b.status === "rascunho" && hasPermission("budget.edit") && <button onClick={() => removeBudget(b)} data-testid={`budget-delete-${b.id}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 className="h-4 w-4" /></button>}
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <FormModal open={newOpen} onOpenChange={setNewOpen} title="Novo Orçamento" onSubmit={createBudget} submitting={saving} testId="budget-new-form" width="max-w-md">
        <TextField label="Exercício (ano)" type="number" value={newYear} onChange={setNewYear} testId="budget-new-year" required />
        <div>
          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Copiar de (opcional)</label>
          <Select value={copyYear || "none"} onValueChange={(v) => setCopyYear(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="budget-new-copy"><SelectValue placeholder="Não copiar" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Não copiar (começar vazio)</SelectItem>
              {[...new Set(budgets.map((b) => b.year))].map((y) => <SelectItem key={y} value={String(y)}>Copiar do exercício {y}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Copy className="h-3 w-3" /> Copia as linhas e valores do orçamento ativo do ano escolhido.</p>
        </div>
      </FormModal>
    </div>
  );
}
